import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import MainPageSortItem from "../../../../pages/CMS/Widgets/components/MainPageSortItem";
import MainPageDragItem from "../../../../pages/CMS/Widgets/components/MainPageDragItem";
import RoundBtn from "components/buttons/RoundBtn";
import { useTranslation } from "react-i18next";

import "./PresetsLobbyTab.scss";

import { useAppDispatch, useAppSelector } from "store";

import {
  getLobbyConfig,
  getCollectionsList,
  getTagsList,
  getBannersList,
  getGamesList,
  getCustomWidgetsList,
  updateLobbyConfig,
  LobbyItem,
} from "store/players/presets/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";
import WidgetsListSkeleton from "components/global/Skeleton/WidgetsListSkeleton/WidgetsListSkeleton";

const ITEM_TYPE = {
  COLLECTION: "collection",
  TAG: "tag",
  BANNERS: "banner",
  GAMES: "game",
  CUSTOM_WIDGET: "customWidget",
};

interface PageItem {
  value: number;
  type: string;
  priority: number;
  label: string;
  img: string;
  showType: boolean;
}

type Props = {
  readOnly?: boolean;
}

const PresetsLobbyTab: React.FC<Props> = ({ readOnly }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();

  const entityId = searchParams.get("entityId");

  const [selectedItems, setSelectedItems] = useState<any[]>([]);
  const [pageItems, setPageItems] = useState<PageItem[]>([]);
  const [selectedItemsToDelete, setSelectedItemsToDelete] = useState<any[]>([]);

  const lobbyConfig = useAppSelector((store) => store.presets.lobbyConfig);

  const collections = useAppSelector((store) => store.presets.collections);

  const tags = useAppSelector((store) => store.presets.tags);

  const banners = useAppSelector((store) => store.presets.banners);

  const games = useAppSelector((store) => store.presets.games);

  const isLobbyFetching = useAppSelector(
    (store) => store.presets.isLobbyFetching,
  );

  const customWidgets = useAppSelector((store) => store.presets.customWidgets);

  const pageItemsCollections = pageItems
    .filter((item) => item.type === ITEM_TYPE.COLLECTION)
    .map((item) => item.value);

  const pageItemsTags = pageItems
    .filter((item) => item.type === ITEM_TYPE.TAG)
    .map((item) => item.value);

  const pageItemsBanners = pageItems
    .filter((item) => item.type === ITEM_TYPE.BANNERS)
    .map((item) => item.value);

  const pageItemsGames = pageItems
    .filter((item) => item.type === ITEM_TYPE.GAMES)
    .map((item) => item.value);

  const pageItemsCustomWidgets = pageItems
    .filter((item) => item.type === ITEM_TYPE.CUSTOM_WIDGET)
    .map((item) => item.value);

  useEffect(() => {
    if (entityId) {
      dispatch(getLobbyConfig(entityId));
    }

    dispatch(getCollectionsList());
    dispatch(getTagsList());
    dispatch(getBannersList());
    dispatch(getGamesList());
    dispatch(getCustomWidgetsList());
  }, [entityId]);

  const getItemLabel = (type: string, id: number) => {
    switch (type) {
      case ITEM_TYPE.COLLECTION:
        return collections.find((el) => el.id === id)?.title || "";

      case ITEM_TYPE.TAG:
        return tags.find((el) => el.id === id)?.title || "";

      case ITEM_TYPE.BANNERS:
        return banners.find((el) => el.id === id)?.title || "";

      case ITEM_TYPE.GAMES:
        return games.find((el) => el.id === id)?.title || "";

      case ITEM_TYPE.CUSTOM_WIDGET:
        return customWidgets.find((el) => el.id === id)?.title || "";

      default:
        return "";
    }
  };

  useEffect(() => {
    if (!lobbyConfig?.items) {
      return;
    }

    const mappedItems = lobbyConfig.items.map((item) => {
      const label = getItemLabel(item.instanceType, item.instanceId);

      return {
        value: item.instanceId,
        type: item.instanceType,
        priority: item.priorityOrder,
        label,
        img: "",
        showType: true,
      };
    });

    setPageItems(mappedItems);
  }, [lobbyConfig, collections, tags, banners, games, customWidgets]);

  const handleUpdateItems = (items: PageItem[]) => {
    if (!entityId) {
      return;
    }

    dispatch(
      updateLobbyConfig({
        id: entityId,
        items: items.map((item, index) => ({
          instanceType: item.type as LobbyItem["instanceType"],
          instanceId: item.value,
          priorityOrder: index + 1,
        })),
      }),
    );
  };

  const moveItem = (dragIndex: number, hoverIndex: number) => {
    const items = Array.from(pageItems);

    const [reorderedItem] = items.splice(dragIndex, 1);

    items.splice(hoverIndex, 0, reorderedItem);

    setPageItems(items);

    handleUpdateItems(items);
  };

  return (
    <div className="preset-form-wrapper">
      <div className="preset-form-content">
        <div className="main-page-wrapper">
          <div className="main-page-content">
            <div className="main-page-widget">
              <div className="main-page-widget--box">
                {isLobbyFetching ? (
                  <WidgetsListSkeleton />
                ) : (
                  <div className="main-page-widget--list">
                    {pageItems.map((el, index) => (
                      <MainPageSortItem
                        isMainWidgetPage
                        handleClick={() => {
                          if (selectedItemsToDelete.includes(el)) {
                            setSelectedItemsToDelete(
                              selectedItemsToDelete.filter(
                                (item) => item !== el,
                              ),
                            );
                          } else {
                            setSelectedItemsToDelete([
                              ...selectedItemsToDelete,
                              el,
                            ]);
                          }
                        }}
                        isSelected={selectedItemsToDelete.includes(el)}
                        key={`${el.value}-${el.type}`}
                        item={el}
                        index={index}
                        moveItem={moveItem}
                        hideImage
                        disableEdit
                        isDraggable={!readOnly}
                        readOnly={readOnly}
                      />
                    ))}
                  </div>
                )}
                <div className="main-page-btns">
                  {!readOnly && (
                    <>
                      <RoundBtn
                        handleClick={() => {
                          const newArray = [...pageItems, ...selectedItems];

                          setSelectedItems([]);
                          setPageItems(newArray);

                          if (!entityId) {
                            return;
                          }

                          handleUpdateItems(newArray);
                          dispatch(
                            showNotification({
                              title: t("success"),
                              message: t("entityUpdateSuccess"),
                              type: NotificationTypes.success,
                            }),
                          );
                        }}
                        txt="+"
                        disabled={selectedItems.length === 0}
                      />

                      <RoundBtn
                        handleClick={() => {
                          const newArray = pageItems.filter(
                            (el) => !selectedItemsToDelete.includes(el),
                          );

                          setSelectedItemsToDelete([]);
                          setPageItems(newArray);

                          if (!entityId) {
                            return;
                          }

                          handleUpdateItems(newArray);
                          dispatch(
                            showNotification({
                              title: t("success"),
                              message: t("entityUpdateSuccess"),
                              type: NotificationTypes.success,
                            }),
                          );
                        }}
                        txt="-"
                        disabled={selectedItemsToDelete.length === 0}
                      />
                    </>
                  )}

                </div>
              </div>
            </div>

            <div className="main-page-lists">
              <div className="main-page-list-box">
                <div className="main-page-title collections">Collections</div>

                <div className="main-page-list-content">
                  {isLobbyFetching ? (
                    <WidgetsListSkeleton />
                  ) : (
                    <>
                      {collections
                        .filter(
                          (item) => !pageItemsCollections.includes(item.id),
                        )
                        .map((el) => (
                          <MainPageDragItem
                            handleClick={() => {
                              if (
                                selectedItems.some(
                                  (item) =>
                                    item.value === el.id &&
                                    item.type === ITEM_TYPE.COLLECTION,
                                )
                              ) {
                                setSelectedItems(
                                  selectedItems.filter(
                                    (item) =>
                                      !(
                                        item.value === el.id &&
                                        item.type === ITEM_TYPE.COLLECTION
                                      ),
                                  ),
                                );
                              } else {
                                setSelectedItems([
                                  ...selectedItems,
                                  {
                                    value: el.id,
                                    label: el.title,
                                    img: "",
                                    type: ITEM_TYPE.COLLECTION,
                                    priority: 0,
                                    showType: true,
                                  },
                                ]);
                              }
                            }}
                            isSelected={selectedItems.some(
                              (item) =>
                                item.value === el.id &&
                                item.type === ITEM_TYPE.COLLECTION,
                            )}
                            key={el.id}
                            item={{
                              value: el.id,
                              label: el.title,
                              img: "",
                              type: ITEM_TYPE.COLLECTION,
                            }}
                            type={ITEM_TYPE.COLLECTION}
                            hideImage
                          />
                        ))}
                    </>
                  )}
                </div>
              </div>

              <div className="main-page-list-box">
                <div className="main-page-title tags">Tags</div>

                <div className="main-page-list-content">
                  {isLobbyFetching ? (
                    <WidgetsListSkeleton />
                  ) : (
                    <>
                      {tags
                        .filter((item) => !pageItemsTags.includes(item.id))
                        .map((el: any) => (
                          <MainPageDragItem
                            handleClick={() => {
                              if (
                                selectedItems.some(
                                  (item) =>
                                    item.value === el.id &&
                                    item.type === ITEM_TYPE.TAG,
                                )
                              ) {
                                setSelectedItems(
                                  selectedItems.filter(
                                    (item) =>
                                      !(
                                        item.value === el.id &&
                                        item.type === ITEM_TYPE.TAG
                                      ),
                                  ),
                                );
                              } else {
                                setSelectedItems([
                                  ...selectedItems,
                                  {
                                    value: el.id,
                                    label: el.title,
                                    img: "",
                                    type: ITEM_TYPE.TAG,
                                    priority: 0,
                                    showType: true,
                                  },
                                ]);
                              }
                            }}
                            isSelected={selectedItems.some(
                              (item) =>
                                item.value === el.id &&
                                item.type === ITEM_TYPE.TAG,
                            )}
                            key={el.id}
                            item={{
                              value: el.id,
                              label: el.title,
                              img: "",
                              type: ITEM_TYPE.TAG,
                            }}
                            type={ITEM_TYPE.TAG}
                            hideImage
                          />
                        ))}
                    </>
                  )}
                </div>
              </div>

              <div className="main-page-list-box">
                <div className="main-page-title banners">Banners</div>

                <div className="main-page-list-content">
                  {isLobbyFetching ? (
                    <WidgetsListSkeleton />
                  ) : (
                    <>
                      {banners
                        .filter((item) => !pageItemsBanners.includes(item.id))
                        .map((el: any) => (
                          <MainPageDragItem
                            handleClick={() => {
                              if (
                                selectedItems.some(
                                  (item) =>
                                    item.value === el.id &&
                                    item.type === ITEM_TYPE.BANNERS,
                                )
                              ) {
                                setSelectedItems(
                                  selectedItems.filter(
                                    (item) =>
                                      !(
                                        item.value === el.id &&
                                        item.type === ITEM_TYPE.BANNERS
                                      ),
                                  ),
                                );
                              } else {
                                setSelectedItems([
                                  ...selectedItems,
                                  {
                                    value: el.id,
                                    label: el.title,
                                    img: "",
                                    type: ITEM_TYPE.BANNERS,
                                    priority: 0,
                                    showType: true,
                                  },
                                ]);
                              }
                            }}
                            isSelected={selectedItems.some(
                              (item) =>
                                item.value === el.id &&
                                item.type === ITEM_TYPE.BANNERS,
                            )}
                            key={el.id}
                            item={{
                              value: el.id,
                              label: el.title,
                              img: "",
                              type: ITEM_TYPE.BANNERS,
                            }}
                            type={ITEM_TYPE.BANNERS}
                            hideImage
                          />
                        ))}
                    </>
                  )}
                </div>
              </div>

              <div className="main-page-list-box">
                <div className="main-page-title games">Games</div>

                <div className="main-page-list-content">
                  {isLobbyFetching ? (
                    <WidgetsListSkeleton />
                  ) : (
                    <>
                      {games
                        .filter((item) => !pageItemsGames.includes(item.id))
                        .map((el: any) => (
                          <MainPageDragItem
                            handleClick={() => {
                              if (
                                selectedItems.some(
                                  (item) =>
                                    item.value === el.id &&
                                    item.type === ITEM_TYPE.GAMES,
                                )
                              ) {
                                setSelectedItems(
                                  selectedItems.filter(
                                    (item) =>
                                      !(
                                        item.value === el.id &&
                                        item.type === ITEM_TYPE.GAMES
                                      ),
                                  ),
                                );
                              } else {
                                setSelectedItems([
                                  ...selectedItems,
                                  {
                                    value: el.id,
                                    label: el.title,
                                    img: "",
                                    type: ITEM_TYPE.GAMES,
                                    priority: 0,
                                    showType: true,
                                  },
                                ]);
                              }
                            }}
                            isSelected={selectedItems.some(
                              (item) =>
                                item.value === el.id &&
                                item.type === ITEM_TYPE.GAMES,
                            )}
                            key={el.id}
                            item={{
                              value: el.id,
                              label: el.title,
                              img: "",
                              type: ITEM_TYPE.GAMES,
                            }}
                            type={ITEM_TYPE.GAMES}
                            hideImage
                          />
                        ))}
                    </>
                  )}
                </div>
              </div>

              <div className="main-page-list-box">
                <div className="main-page-title custom-widgets">Customized</div>

                <div className="main-page-list-content">
                  {isLobbyFetching ? (
                    <WidgetsListSkeleton />
                  ) : (
                    <>
                      {customWidgets
                        .filter(
                          (item) => !pageItemsCustomWidgets.includes(item.id),
                        )
                        .map((el: any) => (
                          <MainPageDragItem
                            handleClick={() => {
                              if (
                                selectedItems.some(
                                  (item) =>
                                    item.value === el.id &&
                                    item.type === ITEM_TYPE.CUSTOM_WIDGET,
                                )
                              ) {
                                setSelectedItems(
                                  selectedItems.filter(
                                    (item) =>
                                      !(
                                        item.value === el.id &&
                                        item.type === ITEM_TYPE.CUSTOM_WIDGET
                                      ),
                                  ),
                                );
                              } else {
                                setSelectedItems([
                                  ...selectedItems,
                                  {
                                    value: el.id,
                                    label: el.title,
                                    img: "",
                                    type: ITEM_TYPE.CUSTOM_WIDGET,
                                    priority: 0,
                                    showType: true,
                                  },
                                ]);
                              }
                            }}
                            isSelected={selectedItems.some(
                              (item) =>
                                item.value === el.id &&
                                item.type === ITEM_TYPE.CUSTOM_WIDGET,
                            )}
                            key={el.id}
                            item={{
                              value: el.id,
                              label: el.title,
                              img: "",
                              type: ITEM_TYPE.CUSTOM_WIDGET,
                            }}
                            type={ITEM_TYPE.CUSTOM_WIDGET}
                            hideImage
                          />
                        ))}
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PresetsLobbyTab;
